import React, { useState, useRef } from 'react';
import {
  Box, Flex, Text, Grid, Button, Input, Textarea, Select,
  VStack, HStack, Table, Thead, Tbody, Tr, Th, Td,
} from '@chakra-ui/react';
import { useApp } from '../hooks/useApp';

const PANELS = [
  { id: 'overview', label: '📊 Overview' },
  { id: 'posts', label: '📝 Posts' },
  { id: 'products', label: '🛒 Products' },
  { id: 'videos', label: '🎬 Videos' },
  { id: 'documents', label: '📄 Documents' },
  { id: 'subscribers', label: '📧 Subscribers' },
  { id: 'inbox', label: '📬 Inbox' },
];

export default function AdminPage() {
  const { adminUser, adminLogin, adminLogout, allArticles, products, videos, documents, subscribers, contacts } = useApp();
  const [activePanel, setActivePanel] = useState('overview');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPass, setLoginPass] = useState('');

  if (!adminUser) return <AdminLogin onLogin={adminLogin} email={loginEmail} setEmail={setLoginEmail} pass={loginPass} setPass={setLoginPass} />;

  return (
    <Box pt="68px" minH="100vh" bg="#07070a">
      <Grid templateColumns={{ base: '1fr', lg: '220px 1fr' }} minH="calc(100vh - 68px)">
        {/* Sidebar */}
        <Box
          bg="#0a0f1a"
          borderRight="1px solid rgba(255,255,255,0.07)"
          p={5}
          display={{ base: 'none', lg: 'block' }}
        >
          <Text fontFamily="'Fraunces', serif" fontSize="0.78rem" fontWeight={600} color="#6b7fa0" letterSpacing="0.1em" textTransform="uppercase" mb={4}>
            Dashboard
          </Text>
          <VStack align="stretch" gap={1}>
            {PANELS.map(panel => (
              <Box
                key={panel.id}
                px={4}
                py={2.5}
                borderRadius="8px"
                cursor="pointer"
                bg={activePanel === panel.id ? 'rgba(0,195,255,0.1)' : 'transparent'}
                color={activePanel === panel.id ? '#00c3ff' : '#6b7fa0'}
                fontWeight={activePanel === panel.id ? 600 : 400}
                fontSize="0.88rem"
                transition="all 0.2s"
                _hover={{ bg: 'rgba(0,195,255,0.07)', color: '#00c3ff' }}
                onClick={() => setActivePanel(panel.id)}
              >
                {panel.label}
              </Box>
            ))}
          </VStack>
          <Box mt="auto" pt={6}>
            <Button
              w="100%"
              bg="transparent"
              color="#6b7fa0"
              border="1px solid rgba(255,255,255,0.07)"
              borderRadius="8px"
              fontSize="0.85rem"
              _hover={{ bg: 'rgba(255,71,71,0.1)', color: '#ff4747', borderColor: '#ff4747' }}
              onClick={adminLogout}
            >
              🚪 Logout
            </Button>
          </Box>
        </Box>

        {/* Main */}
        <Box p={{ base: 4, lg: 7 }} overflowY="auto">
          {/* Mobile panel select */}
          <Select
            display={{ base: 'block', lg: 'none' }}
            mb={5}
            bg="#0a0f1a"
            border="1px solid rgba(255,255,255,0.07)"
            color="#f0f0f5"
            borderRadius="8px"
            value={activePanel}
            onChange={e => setActivePanel(e.target.value)}
          >
            {PANELS.map(p => <option key={p.id} value={p.id} style={{ background: '#0a0f1a' }}>{p.label}</option>)}
          </Select>

          {activePanel === 'overview' && <OverviewPanel articles={allArticles} products={products} videos={videos} documents={documents} subscribers={subscribers} contacts={contacts} />}
          {activePanel === 'posts' && <PostsPanel />}
          {activePanel === 'products' && <ProductsPanel />}
          {activePanel === 'videos' && <VideosPanel />}
          {activePanel === 'documents' && <DocumentsPanel />}
          {activePanel === 'subscribers' && <SubscribersPanel />}
          {activePanel === 'inbox' && <InboxPanel />}
        </Box>
      </Grid>
    </Box>
  );
}

function AdminLogin({ onLogin, email, setEmail, pass, setPass }) {
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setLoading(true);
    await onLogin(email, pass);
    setLoading(false);
  };

  return (
    <Box pt="100px" pb="90px" px="5%">
      <Box maxW="420px" mx="auto" bg="#0a0f1a" border="1px solid rgba(255,255,255,0.07)" borderRadius="16px" p={10}>
        <Text fontFamily="'Fraunces', serif" fontSize="1.8rem" fontWeight={700} mb={1}>Admin Login</Text>
        <Text color="#6b7fa0" fontSize="0.88rem" mb={8}>TechBheekaran Dashboard</Text>
        <VStack gap={4}>
          <Input
            placeholder="Email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            bg="#0e1520"
            border="1px solid rgba(255,255,255,0.07)"
            color="#f0f0f5"
            borderRadius="8px"
            _placeholder={{ color: '#6b7fa0' }}
            _focus={{ borderColor: '#00c3ff', boxShadow: '0 0 0 1px #00c3ff' }}
          />
          <Input
            placeholder="Password"
            type="password"
            value={pass}
            onChange={e => setPass(e.target.value)}
            bg="#0e1520"
            border="1px solid rgba(255,255,255,0.07)"
            color="#f0f0f5"
            borderRadius="8px"
            _placeholder={{ color: '#6b7fa0' }}
            _focus={{ borderColor: '#00c3ff', boxShadow: '0 0 0 1px #00c3ff' }}
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
          />
          <Button
            w="100%"
            bg="#00c3ff"
            color="#000"
            fontWeight={700}
            py={3}
            borderRadius="8px"
            isLoading={loading}
            _hover={{ transform: 'translateY(-2px)', boxShadow: '0 12px 32px rgba(0,195,255,0.4)' }}
            onClick={handleLogin}
          >
            🔐 Login
          </Button>
        </VStack>
        <Box mt={5} p={4} bg="rgba(0,195,255,0.05)" border="1px solid rgba(0,195,255,0.15)" borderRadius="8px">
          <Text color="#6b7fa0" fontSize="0.78rem">⚙️ Configure Firebase in <code style={{ color: '#00c3ff' }}>src/firebase/config.js</code> to enable authentication.</Text>
        </Box>
      </Box>
    </Box>
  );
}

function StatCard({ emoji, label, value }) {
  return (
    <Box bg="#0a0f1a" border="1px solid rgba(255,255,255,0.07)" borderRadius="12px" p={5.5}>
      <Text fontSize="1.5rem" mb={2}>{emoji}</Text>
      <Text fontFamily="'Fraunces', serif" fontSize="1.7rem" fontWeight={900} color="#00c3ff">{value}</Text>
      <Text fontSize="0.8rem" color="#6b7fa0">{label}</Text>
    </Box>
  );
}

function AdminCard({ children }) {
  return (
    <Box bg="#0a0f1a" border="1px solid rgba(255,255,255,0.07)" borderRadius="14px" overflow="hidden">
      {children}
    </Box>
  );
}

function OverviewPanel({ articles, products, videos, documents, subscribers, contacts }) {
  return (
    <Box>
      <Text fontFamily="'Fraunces', serif" fontSize="1.5rem" fontWeight={700} mb={6}>Dashboard Overview</Text>
      <Grid templateColumns={{ base: '1fr 1fr', lg: 'repeat(5,1fr)' }} gap={4} mb={8}>
        <StatCard emoji="📝" label="Total Articles" value={articles.length} />
        <StatCard emoji="🛒" label="Products Listed" value={products.length} />
        <StatCard emoji="🎬" label="Videos Added" value={videos.length} />
        <StatCard emoji="📄" label="Documents" value={documents.length} />
        <StatCard emoji="📧" label="Subscribers" value={subscribers.length} />
      </Grid>
      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={5}>
        <AdminCard>
          <Box p={5} borderBottom="1px solid rgba(255,255,255,0.07)">
            <Text fontFamily="'Fraunces', serif" fontWeight={700}>Recent Articles</Text>
          </Box>
          <Box>
            {articles.slice(0, 5).map(a => (
              <Flex key={a.id} px={5} py={3.5} borderBottom="1px solid rgba(255,255,255,0.04)" align="center" justify="space-between">
                <Text fontSize="0.88rem" fontWeight={500} noOfLines={1}>{a.emoji} {a.title}</Text>
                <Box
                  px={2}
                  py={0.5}
                  borderRadius="full"
                  fontSize="0.68rem"
                  fontWeight={600}
                  bg={a.status === 'published' ? 'rgba(74,222,128,0.15)' : 'rgba(0,195,255,0.1)'}
                  color={a.status === 'published' ? '#4ade80' : '#00c3ff'}
                >
                  {a.status}
                </Box>
              </Flex>
            ))}
          </Box>
        </AdminCard>
        <AdminCard>
          <Box p={5} borderBottom="1px solid rgba(255,255,255,0.07)">
            <Text fontFamily="'Fraunces', serif" fontWeight={700}>Recent Inbox ({contacts.length})</Text>
          </Box>
          <Box>
            {contacts.slice(0, 5).map((c, i) => (
              <Flex key={c.id || i} px={5} py={3.5} borderBottom="1px solid rgba(255,255,255,0.04)" align="center" gap={3}>
                <Box w="7px" h="7px" bg={c.read ? '#6b7fa0' : '#00c3ff'} borderRadius="50%" flexShrink={0} />
                <Box flex={1} minW={0}>
                  <Text fontSize="0.88rem" fontWeight={600}>{c.name}</Text>
                  <Text fontSize="0.78rem" color="#6b7fa0" noOfLines={1}>{c.subject || c.category}</Text>
                </Box>
              </Flex>
            ))}
            {contacts.length === 0 && (
              <Box py={8} textAlign="center" color="#6b7fa0">
                <Text fontSize="2rem" mb={2}>📬</Text>
                <Text fontSize="0.88rem">No messages yet.</Text>
              </Box>
            )}
          </Box>
        </AdminCard>
      </Grid>
    </Box>
  );
}

function PostsPanel() {
  const { allArticles, saveArticle, deleteArticle } = useApp();
  const [editingId, setEditingId] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [form, setForm] = useState({ title: '', excerpt: '', category: 'Smartphones', emoji: '📱', tags: [], content: '', status: 'published', date: new Date().toLocaleDateString('en', { month: 'short', year: 'numeric' }), readTime: '5 min', gradient: 'linear-gradient(135deg,#1a1a2e,#16213e)' });
  const [tagInput, setTagInput] = useState('');

  const handleSave = async (status) => {
    await saveArticle({ ...form, status }, editingId);
    setShowEditor(false);
    setEditingId(null);
    setForm({ title: '', excerpt: '', category: 'Smartphones', emoji: '📱', tags: [], content: '', status: 'published', date: new Date().toLocaleDateString('en', { month: 'short', year: 'numeric' }), readTime: '5 min', gradient: 'linear-gradient(135deg,#1a1a2e,#16213e)' });
  };

  const handleEdit = (article) => {
    setForm({ ...article });
    setEditingId(article.id);
    setShowEditor(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm(f => ({ ...f, tags: [...f.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Text fontFamily="'Fraunces', serif" fontSize="1.5rem" fontWeight={700}>Posts & Articles</Text>
        <Button
          bg="#00c3ff"
          color="#000"
          fontWeight={700}
          fontSize="0.85rem"
          px={4}
          py={2}
          borderRadius="8px"
          _hover={{ transform: 'translateY(-1px)' }}
          onClick={() => { setShowEditor(true); setEditingId(null); }}
        >
          + New Post
        </Button>
      </Flex>

      {showEditor && (
        <AdminCard>
          <Box p={5} borderBottom="1px solid rgba(255,255,255,0.07)">
            <Text fontFamily="'Fraunces', serif" fontWeight={700}>{editingId ? 'Edit Post' : 'New Post'}</Text>
          </Box>
          <Box p={5}>
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4} mb={4}>
              <Input placeholder="Title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} bg="#0e1520" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="8px" _placeholder={{ color: '#6b7fa0' }} _focus={{ borderColor: '#00c3ff' }} />
              <Select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} bg="#0e1520" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="8px">
                {['Smartphones', 'Laptops', 'Audio', 'Smartwatches', 'Cameras', 'AI Tools'].map(c => <option key={c} value={c} style={{ background: '#0e1520' }}>{c}</option>)}
              </Select>
              <Input placeholder="Emoji (e.g. 📱)" value={form.emoji} onChange={e => setForm(f => ({ ...f, emoji: e.target.value }))} bg="#0e1520" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="8px" _placeholder={{ color: '#6b7fa0' }} _focus={{ borderColor: '#00c3ff' }} />
              <Input placeholder="Read time (e.g. 5 min)" value={form.readTime} onChange={e => setForm(f => ({ ...f, readTime: e.target.value }))} bg="#0e1520" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="8px" _placeholder={{ color: '#6b7fa0' }} _focus={{ borderColor: '#00c3ff' }} />
            </Grid>
            <Textarea placeholder="Short excerpt" value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))} rows={2} bg="#0e1520" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="8px" mb={4} _placeholder={{ color: '#6b7fa0' }} _focus={{ borderColor: '#00c3ff' }} />
            <Textarea placeholder="HTML content" value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} rows={10} bg="#07070a" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="8px" mb={4} fontFamily="monospace" fontSize="0.85rem" _placeholder={{ color: '#6b7fa0' }} _focus={{ borderColor: '#00c3ff' }} />
            <Flex gap={2} mb={4}>
              <Input placeholder="Add tag, press Enter" value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} bg="#0e1520" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="8px" _placeholder={{ color: '#6b7fa0' }} _focus={{ borderColor: '#00c3ff' }} />
              <Button onClick={addTag} bg="transparent" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="8px" _hover={{ borderColor: '#00c3ff' }}>Add</Button>
            </Flex>
            <Flex gap={2} flexWrap="wrap" mb={5}>
              {form.tags.map(tag => (
                <Flex key={tag} align="center" gap={1.5} bg="#0e1520" border="1px solid rgba(255,255,255,0.07)" borderRadius="full" px={3} py={1} fontSize="0.78rem">
                  <Text>{tag}</Text>
                  <Text cursor="pointer" color="#6b7fa0" _hover={{ color: '#ff4747' }} onClick={() => setForm(f => ({ ...f, tags: f.tags.filter(t => t !== tag) }))}>×</Text>
                </Flex>
              ))}
            </Flex>
            <Flex gap={3}>
              <Button bg="#00c3ff" color="#000" fontWeight={700} px={5} py={2.5} borderRadius="8px" _hover={{ transform: 'translateY(-1px)' }} onClick={() => handleSave('published')}>🚀 Publish</Button>
              <Button bg="transparent" color="#f0f0f5" border="1px solid rgba(255,255,255,0.07)" px={5} py={2.5} borderRadius="8px" _hover={{ borderColor: '#00c3ff', color: '#00c3ff' }} onClick={() => handleSave('draft')}>💾 Save Draft</Button>
              <Button bg="transparent" color="#6b7fa0" px={5} py={2.5} borderRadius="8px" _hover={{ color: '#ff4747' }} onClick={() => { setShowEditor(false); setEditingId(null); }}>Cancel</Button>
            </Flex>
          </Box>
        </AdminCard>
      )}

      <Box mt={showEditor ? 6 : 0}>
        <AdminCard>
          <Box overflowX="auto">
            <Table variant="unstyled" size="sm">
              <Thead>
                <Tr>
                  <Th py={3} px={4} fontSize="0.78rem" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">Title</Th>
                  <Th py={3} px={4} fontSize="0.78rem" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">Category</Th>
                  <Th py={3} px={4} fontSize="0.78rem" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">Status</Th>
                  <Th py={3} px={4} fontSize="0.78rem" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {allArticles.map(article => (
                  <Tr key={article.id} _hover={{ bg: 'rgba(255,255,255,0.02)' }}>
                    <Td py={3.5} px={4} borderBottom="1px solid rgba(255,255,255,0.04)">
                      <Flex align="center" gap={2}>
                        <Text>{article.emoji}</Text>
                        <Text fontSize="0.88rem" fontWeight={600} noOfLines={1}>{article.title}</Text>
                      </Flex>
                    </Td>
                    <Td py={3.5} px={4} borderBottom="1px solid rgba(255,255,255,0.04)">
                      <Box px={2.5} py={0.5} bg="rgba(0,195,255,0.15)" color="#00c3ff" borderRadius="full" fontSize="0.7rem" fontWeight={600} display="inline-block">{article.category}</Box>
                    </Td>
                    <Td py={3.5} px={4} borderBottom="1px solid rgba(255,255,255,0.04)">
                      <Flex align="center" gap={1.5}>
                        <Box w="7px" h="7px" borderRadius="50%" bg={article.status === 'published' ? '#4ade80' : '#00c3ff'} />
                        <Text fontSize="0.82rem" color="#6b7fa0">{article.status}</Text>
                      </Flex>
                    </Td>
                    <Td py={3.5} px={4} borderBottom="1px solid rgba(255,255,255,0.04)">
                      <Flex gap={2}>
                        <Button size="sm" bg="transparent" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="6px" fontSize="0.75rem" px={3} py={1.5} _hover={{ borderColor: '#00c3ff', color: '#00c3ff' }} onClick={() => handleEdit(article)}>✏️ Edit</Button>
                        <Button size="sm" bg="#ff4747" color="#fff" borderRadius="6px" fontSize="0.75rem" px={3} py={1.5} _hover={{ bg: '#ff2020' }} onClick={() => deleteArticle(article.id)}>🗑</Button>
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </AdminCard>
      </Box>
    </Box>
  );
}

function ProductsPanel() {
  const { products, saveProduct, deleteProduct } = useApp();
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', emoji: '📦', desc: '', price: '', oldPrice: '', link: '', badge: '', rating: 4.5, gradient: 'linear-gradient(135deg,#1a1a2e,#16213e)' });

  const handleSave = async () => {
    await saveProduct(form, editingId);
    setEditingId(null);
    setForm({ name: '', emoji: '📦', desc: '', price: '', oldPrice: '', link: '', badge: '', rating: 4.5, gradient: 'linear-gradient(135deg,#1a1a2e,#16213e)' });
  };

  return (
    <Box>
      <Text fontFamily="'Fraunces', serif" fontSize="1.5rem" fontWeight={700} mb={6}>Products</Text>
      <AdminCard>
        <Box p={5} borderBottom="1px solid rgba(255,255,255,0.07)">
          <Text fontWeight={700}>{editingId ? 'Edit Product' : 'Add Product'}</Text>
        </Box>
        <Box p={5}>
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4} mb={4}>
            <Input placeholder="Product name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} bg="#0e1520" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="8px" _placeholder={{ color: '#6b7fa0' }} _focus={{ borderColor: '#00c3ff' }} />
            <Input placeholder="Affiliate link *" value={form.link} onChange={e => setForm(f => ({ ...f, link: e.target.value }))} bg="#0e1520" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="8px" _placeholder={{ color: '#6b7fa0' }} _focus={{ borderColor: '#00c3ff' }} />
            <Input placeholder="Emoji (e.g. 📱)" value={form.emoji} onChange={e => setForm(f => ({ ...f, emoji: e.target.value }))} bg="#0e1520" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="8px" _placeholder={{ color: '#6b7fa0' }} _focus={{ borderColor: '#00c3ff' }} />
            <Input placeholder="Price (e.g. ₹12,999)" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} bg="#0e1520" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="8px" _placeholder={{ color: '#6b7fa0' }} _focus={{ borderColor: '#00c3ff' }} />
            <Input placeholder="Old price (optional)" value={form.oldPrice} onChange={e => setForm(f => ({ ...f, oldPrice: e.target.value }))} bg="#0e1520" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="8px" _placeholder={{ color: '#6b7fa0' }} _focus={{ borderColor: '#00c3ff' }} />
            <Select value={form.badge} onChange={e => setForm(f => ({ ...f, badge: e.target.value }))} bg="#0e1520" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="8px">
              <option value="" style={{ background: '#0e1520' }}>No badge</option>
              <option value="pick" style={{ background: '#0e1520' }}>⭐ My Pick</option>
              <option value="hot" style={{ background: '#0e1520' }}>🔥 Hot Deal</option>
              <option value="new" style={{ background: '#0e1520' }}>✨ New</option>
            </Select>
          </Grid>
          <Textarea placeholder="Description" value={form.desc} onChange={e => setForm(f => ({ ...f, desc: e.target.value }))} rows={3} bg="#0e1520" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="8px" mb={4} _placeholder={{ color: '#6b7fa0' }} _focus={{ borderColor: '#00c3ff' }} />
          <Flex gap={3}>
            <Button bg="#00c3ff" color="#000" fontWeight={700} px={5} py={2.5} borderRadius="8px" _hover={{ transform: 'translateY(-1px)' }} onClick={handleSave}>
              {editingId ? '✅ Update' : '🚀 Add Product'}
            </Button>
            {editingId && (
              <Button bg="transparent" color="#6b7fa0" px={5} py={2.5} borderRadius="8px" _hover={{ color: '#ff4747' }} onClick={() => { setEditingId(null); setForm({ name: '', emoji: '📦', desc: '', price: '', oldPrice: '', link: '', badge: '', rating: 4.5, gradient: 'linear-gradient(135deg,#1a1a2e,#16213e)' }); }}>Cancel</Button>
            )}
          </Flex>
        </Box>
      </AdminCard>

      <Box mt={6}>
        <AdminCard>
          <Box overflowX="auto">
            <Table variant="unstyled" size="sm">
              <Thead>
                <Tr>
                  <Th py={3} px={4} fontSize="0.78rem" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">Product</Th>
                  <Th py={3} px={4} fontSize="0.78rem" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">Price</Th>
                  <Th py={3} px={4} fontSize="0.78rem" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">Badge</Th>
                  <Th py={3} px={4} fontSize="0.78rem" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {products.map(p => (
                  <Tr key={p.id} _hover={{ bg: 'rgba(255,255,255,0.02)' }}>
                    <Td py={3.5} px={4} borderBottom="1px solid rgba(255,255,255,0.04)">
                      <Text fontSize="0.88rem" fontWeight={600}>{p.emoji} {p.name}</Text>
                    </Td>
                    <Td py={3.5} px={4} borderBottom="1px solid rgba(255,255,255,0.04)">
                      <Text color="#00c3ff" fontWeight={700}>{p.price}</Text>
                    </Td>
                    <Td py={3.5} px={4} borderBottom="1px solid rgba(255,255,255,0.04)">
                      <Text color="#6b7fa0" fontSize="0.82rem">{p.badge || '—'}</Text>
                    </Td>
                    <Td py={3.5} px={4} borderBottom="1px solid rgba(255,255,255,0.04)">
                      <Flex gap={2}>
                        <Button size="sm" bg="transparent" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="6px" fontSize="0.75rem" px={3} _hover={{ borderColor: '#00c3ff', color: '#00c3ff' }} onClick={() => { setForm({ ...p }); setEditingId(p.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>✏️</Button>
                        <Button size="sm" bg="#ff4747" color="#fff" borderRadius="6px" fontSize="0.75rem" px={3} _hover={{ bg: '#ff2020' }} onClick={() => deleteProduct(p.id)}>🗑</Button>
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </AdminCard>
      </Box>
    </Box>
  );
}

function VideosPanel() {
  const { videos, saveVideo, deleteVideo } = useApp();
  const [form, setForm] = useState({ title: '', url: '', category: 'Smartphones', emoji: '🎬', badge: '', views: '0', duration: '—', gradient: 'linear-gradient(135deg,#1a1a2e,#0f3460)' });

  const handleSave = async () => {
    await saveVideo(form);
    setForm({ title: '', url: '', category: 'Smartphones', emoji: '🎬', badge: '', views: '0', duration: '—', gradient: 'linear-gradient(135deg,#1a1a2e,#0f3460)' });
  };

  return (
    <Box>
      <Text fontFamily="'Fraunces', serif" fontSize="1.5rem" fontWeight={700} mb={6}>Videos</Text>
      <AdminCard>
        <Box p={5} borderBottom="1px solid rgba(255,255,255,0.07)"><Text fontWeight={700}>Add Video</Text></Box>
        <Box p={5}>
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4} mb={4}>
            <Input placeholder="Title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} bg="#0e1520" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="8px" _placeholder={{ color: '#6b7fa0' }} _focus={{ borderColor: '#00c3ff' }} />
            <Input placeholder="YouTube URL *" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} bg="#0e1520" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="8px" _placeholder={{ color: '#6b7fa0' }} _focus={{ borderColor: '#00c3ff' }} />
            <Select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} bg="#0e1520" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="8px">
              {['Smartphones', 'Laptops', 'Audio', 'Smartwatches', 'AI Tools', 'Cameras'].map(c => <option key={c} value={c} style={{ background: '#0e1520' }}>{c}</option>)}
            </Select>
            <Input placeholder="Duration (e.g. 12 min)" value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} bg="#0e1520" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" borderRadius="8px" _placeholder={{ color: '#6b7fa0' }} _focus={{ borderColor: '#00c3ff' }} />
          </Grid>
          <Button bg="#00c3ff" color="#000" fontWeight={700} px={5} py={2.5} borderRadius="8px" _hover={{ transform: 'translateY(-1px)' }} onClick={handleSave}>🚀 Add Video</Button>
        </Box>
      </AdminCard>

      <Box mt={6}>
        <AdminCard>
          <Box overflowX="auto">
            <Table variant="unstyled" size="sm">
              <Thead>
                <Tr>
                  <Th py={3} px={4} fontSize="0.78rem" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">Title</Th>
                  <Th py={3} px={4} fontSize="0.78rem" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">Category</Th>
                  <Th py={3} px={4} fontSize="0.78rem" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">Views</Th>
                  <Th py={3} px={4} fontSize="0.78rem" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {videos.map(v => (
                  <Tr key={v.id} _hover={{ bg: 'rgba(255,255,255,0.02)' }}>
                    <Td py={3.5} px={4} borderBottom="1px solid rgba(255,255,255,0.04)">
                      <Text fontSize="0.88rem" fontWeight={600}>{v.emoji} {v.title}</Text>
                    </Td>
                    <Td py={3.5} px={4} borderBottom="1px solid rgba(255,255,255,0.04)">
                      <Box px={2.5} py={0.5} bg="rgba(0,195,255,0.15)" color="#00c3ff" borderRadius="full" fontSize="0.7rem" fontWeight={600} display="inline-block">{v.category}</Box>
                    </Td>
                    <Td py={3.5} px={4} borderBottom="1px solid rgba(255,255,255,0.04)">
                      <Text color="#6b7fa0" fontSize="0.82rem">{v.views}</Text>
                    </Td>
                    <Td py={3.5} px={4} borderBottom="1px solid rgba(255,255,255,0.04)">
                      <Button size="sm" bg="#ff4747" color="#fff" borderRadius="6px" fontSize="0.75rem" px={3} _hover={{ bg: '#ff2020' }} onClick={() => deleteVideo(v.id)}>🗑</Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </AdminCard>
      </Box>
    </Box>
  );
}

function SubscribersPanel() {
  const { subscribers } = useApp();

  const exportCSV = () => {
    const csv = ['Name,Email', ...subscribers.map(s => `${s.name},${s.email}`)].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'subscribers.csv';
    a.click();
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Text fontFamily="'Fraunces', serif" fontSize="1.5rem" fontWeight={700}>Subscribers ({subscribers.length})</Text>
        <Button bg="transparent" border="1px solid rgba(255,255,255,0.07)" color="#f0f0f5" fontWeight={600} fontSize="0.85rem" px={4} py={2} borderRadius="8px" _hover={{ borderColor: '#00c3ff', color: '#00c3ff' }} onClick={exportCSV}>
          📥 Export CSV
        </Button>
      </Flex>
      <AdminCard>
        <Box overflowX="auto">
          <Table variant="unstyled" size="sm">
            <Thead>
              <Tr>
                <Th py={3} px={4} fontSize="0.78rem" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">#</Th>
                <Th py={3} px={4} fontSize="0.78rem" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">Name</Th>
                <Th py={3} px={4} fontSize="0.78rem" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">Email</Th>
              </Tr>
            </Thead>
            <Tbody>
              {subscribers.map((s, i) => (
                <Tr key={s.id || i} _hover={{ bg: 'rgba(255,255,255,0.02)' }}>
                  <Td py={3} px={4} borderBottom="1px solid rgba(255,255,255,0.04)" color="#6b7fa0" fontSize="0.82rem">{i + 1}</Td>
                  <Td py={3} px={4} borderBottom="1px solid rgba(255,255,255,0.04)" fontSize="0.88rem" fontWeight={500}>{s.name}</Td>
                  <Td py={3} px={4} borderBottom="1px solid rgba(255,255,255,0.04)" color="#00c3ff" fontSize="0.82rem">{s.email}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          {subscribers.length === 0 && (
            <Box py={10} textAlign="center" color="#6b7fa0">
              <Text fontSize="2.5rem" mb={2}>📧</Text>
              <Text>No subscribers yet.</Text>
            </Box>
          )}
        </Box>
      </AdminCard>
    </Box>
  );
}

function InboxPanel() {
  const { contacts } = useApp();
  const [selected, setSelected] = useState(null);

  return (
    <Box>
      <Text fontFamily="'Fraunces', serif" fontSize="1.5rem" fontWeight={700} mb={6}>Inbox ({contacts.length})</Text>
      {contacts.length === 0 ? (
        <Box textAlign="center" py={16} color="#6b7fa0">
          <Text fontSize="3rem" mb={3}>📬</Text>
          <Text>No messages yet.</Text>
        </Box>
      ) : (
        <Box>
          {contacts.map((c, i) => (
            <Box key={c.id || i}>
              <Box
                bg="#0a0f1a"
                border="1px solid rgba(255,255,255,0.07)"
                borderLeft={!c.read ? '3px solid #00c3ff' : '3px solid transparent'}
                borderRadius="10px"
                p={4}
                mb={3}
                cursor="pointer"
                transition="border-color 0.2s"
                _hover={{ borderColor: 'rgba(0,195,255,0.3)' }}
                onClick={() => setSelected(selected === i ? null : i)}
              >
                <Flex justify="space-between" align="center">
                  <Text fontWeight={600} fontSize="0.9rem">{c.name}</Text>
                  <Text color="#6b7fa0" fontSize="0.75rem">{c.category}</Text>
                </Flex>
                <Text color="#6b7fa0" fontSize="0.82rem" mt={0.5}>{c.subject || 'No subject'}</Text>
                <Text color="#6b7fa0" fontSize="0.82rem" mt={1.5} noOfLines={1}>{c.message}</Text>
              </Box>
              {selected === i && (
                <Box bg="#0e1520" border="1px solid rgba(255,255,255,0.07)" borderRadius="12px" p={6} mb={4}>
                  <Text fontWeight={600} mb={1}>{c.name} — {c.email}</Text>
                  <Text color="#6b7fa0" fontSize="0.82rem" mb={4}>{c.subject} · {c.category}</Text>
                  <Text color="#b8b8d0" lineHeight={1.8}>{c.message}</Text>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

const DOCUMENT_CATEGORIES = ['General', 'Smartphones', 'Laptops', 'Audio', 'Smartwatches', 'Cameras', 'AI Tools'];
const EMPTY_DOC_FORM = { title: '', description: '', fileUrl: '', videoTitle: '', category: 'General', emoji: '📄' };

function DocumentsPanel() {
  const { documents, saveDocument, deleteDocument, showToast } = useApp();
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_DOC_FORM });
  const [copied, setCopied] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null); // null | 0-100
  const [uploadError, setUploadError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const stuckTimerRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setUploadError(null);
    const ext = file.name.split('.').pop().toLowerCase();
    const emojiMap = { pdf: '📄', doc: '📝', docx: '📝', xls: '📊', xlsx: '📊', ppt: '📊', pptx: '📊', zip: '🗜️', csv: '📋' };
    setForm(f => ({ ...f, emoji: emojiMap[ext] || '📄' }));
  };

  const handleUploadFile = async () => {
    if (!selectedFile) return;
    setUploadError(null);

    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
    const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

    if (!cloudName || cloudName === 'your_cloud_name' || !uploadPreset || uploadPreset === 'your_unsigned_preset') {
      setUploadError('not_configured');
      return;
    }

    setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('upload_preset', uploadPreset);
    formData.append('folder', 'techbheekaran_docs');

    try {
      // Use XMLHttpRequest to track real upload progress
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `https://api.cloudinary.com/v1_1/${cloudName}/raw/upload`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status === 200) {
            const data = JSON.parse(xhr.responseText);
            setForm(f => ({ ...f, fileUrl: data.secure_url }));
            setUploadProgress(null);
            setSelectedFile(null);
            showToast('File uploaded successfully!', '✅');
            resolve();
          } else {
            reject(new Error(`Upload failed: ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(formData);
      });
    } catch (err) {
      setUploadProgress(null);
      setUploadError('failed');
      showToast('Upload failed. Check Cloudinary config.', '❌', 'error');
    }
  };

  const handleSave = async () => {
    if (!form.title) return showToast('Title is required.', '⚠️', 'warning');
    if (!form.fileUrl) return showToast('Upload a file or paste a URL.', '⚠️', 'warning');
    await saveDocument(form, editingId);
    setEditingId(null);
    setForm({ ...EMPTY_DOC_FORM });
    setSelectedFile(null);
  };

  const handleEdit = (doc) => {
    setForm({ ...doc });
    setEditingId(doc.id);
    setSelectedFile(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm({ ...EMPTY_DOC_FORM });
    setSelectedFile(null);
    setUploadProgress(null);
  };

  const copyLink = (id) => {
    const url = `${(process.env.REACT_APP_SITE_URL || window.location.origin)}/docs#${id}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Text fontFamily="'Fraunces', serif" fontSize="1.5rem" fontWeight={700}>Documents & Resources</Text>
      </Flex>

      {/* Info Banner */}
      <Box
        bg="rgba(0,195,255,0.06)"
        border="1px solid rgba(0,195,255,0.2)"
        borderRadius="12px"
        p={4}
        mb={6}
      >
        <Flex align="flex-start" gap={3}>
          <Text fontSize="1.2rem" flexShrink={0}>💡</Text>
          <Box>
            <Text fontWeight={600} fontSize="0.9rem" mb={1}>YouTube Document Links</Text>
            <Text color="#6b7fa0" fontSize="0.83rem" lineHeight={1.6}>
              Upload documents here. Each gets a unique link like <Text as="span" color="#00c3ff" fontFamily="monospace">{(process.env.REACT_APP_SITE_URL || window.location.origin)}/docs#[id]</Text>.
              Copy & paste this link into your YouTube video description. Viewers will land directly on this document on the Resources page.
            </Text>
          </Box>
        </Flex>
      </Box>

      {/* Form */}
      <AdminCard>
        <Box p={5} borderBottom="1px solid rgba(255,255,255,0.07)">
          <Text fontWeight={700}>{editingId ? 'Edit Document' : 'Add Document'}</Text>
        </Box>
        <Box p={5}>
          {/* Row 1: Title + Video */}
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4} mb={4}>
            <Input
              placeholder="Document title *"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              bg="#0e1520"
              border="1px solid rgba(0,195,255,0.15)"
              color="#f0f0f5"
              borderRadius="8px"
              _placeholder={{ color: '#6b7fa0' }}
              _focus={{ borderColor: '#00c3ff' }}
            />
            <Input
              placeholder="Related YouTube video title"
              value={form.videoTitle}
              onChange={e => setForm(f => ({ ...f, videoTitle: e.target.value }))}
              bg="#0e1520"
              border="1px solid rgba(0,195,255,0.15)"
              color="#f0f0f5"
              borderRadius="8px"
              _placeholder={{ color: '#6b7fa0' }}
              _focus={{ borderColor: '#00c3ff' }}
            />
          </Grid>

          {/* Row 2: Category + Emoji */}
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4} mb={4}>
            <Select
              value={form.category}
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              bg="#0e1520"
              border="1px solid rgba(0,195,255,0.15)"
              color="#f0f0f5"
              borderRadius="8px"
            >
              {DOCUMENT_CATEGORIES.map(c => <option key={c} value={c} style={{ background: '#0e1520' }}>{c}</option>)}
            </Select>
            <Input
              placeholder="Emoji (e.g. 📄 📊 📋)"
              value={form.emoji}
              onChange={e => setForm(f => ({ ...f, emoji: e.target.value }))}
              bg="#0e1520"
              border="1px solid rgba(0,195,255,0.15)"
              color="#f0f0f5"
              borderRadius="8px"
              _placeholder={{ color: '#6b7fa0' }}
              _focus={{ borderColor: '#00c3ff' }}
            />
          </Grid>

          {/* Description */}
          <Textarea
            placeholder="Description (optional)"
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            rows={2}
            bg="#0e1520"
            border="1px solid rgba(0,195,255,0.15)"
            color="#f0f0f5"
            borderRadius="8px"
            mb={4}
            _placeholder={{ color: '#6b7fa0' }}
            _focus={{ borderColor: '#00c3ff' }}
          />

          {/* File Upload Section */}
          <Box
            bg="#07070a"
            border="1px solid rgba(0,195,255,0.2)"
            borderRadius="12px"
            p={5}
            mb={4}
          >
            <Text fontSize="0.82rem" fontWeight={600} color="#6b7fa0" mb={3} textTransform="uppercase" letterSpacing="0.08em">
              Document File *
            </Text>

            {/* Hidden native file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.zip,.txt"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />

            {/* Upload area */}
            <Flex
              direction={{ base: 'column', sm: 'row' }}
              align={{ base: 'stretch', sm: 'center' }}
              gap={3}
              mb={3}
            >
              <Button
                onClick={() => fileInputRef.current?.click()}
                bg="rgba(0,195,255,0.1)"
                color="#00c3ff"
                border="1px solid rgba(0,195,255,0.3)"
                fontWeight={600}
                fontSize="0.88rem"
                px={5}
                py={2.5}
                borderRadius="8px"
                flexShrink={0}
                _hover={{ bg: 'rgba(0,195,255,0.18)', borderColor: '#00c3ff' }}
              >
                📁 Choose File
              </Button>

              <Box flex={1} minW={0}>
                {selectedFile ? (
                  <Flex align="center" gap={2} bg="#0e1520" border="1px solid rgba(0,195,255,0.2)" borderRadius="8px" px={3} py={2}>
                    <Text fontSize="1rem" flexShrink={0}>
                      {selectedFile.name.split('.').pop().toLowerCase() === 'pdf' ? '📄' : '📝'}
                    </Text>
                    <Text fontSize="0.82rem" color="#f0f0f5" fontWeight={500} noOfLines={1} flex={1}>
                      {selectedFile.name}
                    </Text>
                    <Text fontSize="0.75rem" color="#6b7fa0" flexShrink={0}>
                      {(selectedFile.size / 1024 / 1024).toFixed(1)} MB
                    </Text>
                    <Text
                      fontSize="0.8rem"
                      color="#6b7fa0"
                      cursor="pointer"
                      flexShrink={0}
                      _hover={{ color: '#ff4747' }}
                      onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    >
                      ✕
                    </Text>
                  </Flex>
                ) : form.fileUrl ? (
                  <Flex align="center" gap={2} bg="#0e1520" border="1px solid rgba(74,222,128,0.25)" borderRadius="8px" px={3} py={2}>
                    <Text fontSize="0.85rem">✅</Text>
                    <Text fontSize="0.82rem" color="#4ade80" noOfLines={1} flex={1}>File already uploaded</Text>
                    <Text
                      fontSize="0.75rem"
                      color="#6b7fa0"
                      cursor="pointer"
                      onClick={() => setForm(f => ({ ...f, fileUrl: '' }))}
                      _hover={{ color: '#ff4747' }}
                    >
                      Replace
                    </Text>
                  </Flex>
                ) : (
                  <Flex align="center" gap={2} bg="#0e1520" border="1px dashed rgba(0,195,255,0.15)" borderRadius="8px" px={3} py={2}>
                    <Text fontSize="0.82rem" color="#6b7fa0">No file chosen — supports PDF, Word, Excel, PPT, CSV</Text>
                  </Flex>
                )}
              </Box>

              {selectedFile && uploadProgress === null && !uploadError && (
                <Button
                  onClick={handleUploadFile}
                  bg="#00c3ff"
                  color="#000"
                  fontWeight={700}
                  fontSize="0.85rem"
                  px={5}
                  py={2.5}
                  borderRadius="8px"
                  flexShrink={0}
                  _hover={{ bg: '#33d0ff' }}
                >
                  ⬆ Upload
                </Button>
              )}
            </Flex>

            {/* Upload progress bar */}
            {uploadProgress !== null && (
              <Box mt={3}>
                <Flex justify="space-between" mb={1.5}>
                  <Text fontSize="0.78rem" color="#6b7fa0">
                    {uploadProgress === 0 ? 'Connecting to Firebase Storage...' : 'Uploading...'}
                  </Text>
                  <Text fontSize="0.78rem" color="#00c3ff" fontWeight={600}>{uploadProgress}%</Text>
                </Flex>
                <Box bg="rgba(0,195,255,0.1)" borderRadius="full" h="7px" overflow="hidden">
                  <Box
                    h="100%"
                    bgGradient="linear(to-r, #0077ff, #00c3ff)"
                    borderRadius="full"
                    style={{ width: `${Math.max(uploadProgress, 3)}%`, transition: 'width 0.4s ease' }}
                  />
                </Box>
              </Box>
            )}

            {/* Error panel */}
            {uploadError && (
              <Box
                mt={3}
                bg={uploadError === 'not_configured' ? 'rgba(245,158,11,0.07)' : 'rgba(255,71,71,0.07)'}
                border={`1px solid ${uploadError === 'not_configured' ? 'rgba(245,158,11,0.35)' : 'rgba(255,71,71,0.3)'}`}
                borderRadius="10px"
                p={4}
              >
                {uploadError === 'not_configured' && (
                  <VStack align="flex-start" gap={2}>
                    <Text fontSize="0.88rem" fontWeight={700} color="#f59e0b">
                      ⚙️ Cloudinary not set up yet — 2 minute free setup
                    </Text>
                    <VStack align="flex-start" gap={1} w="100%">
                      <Text fontSize="0.8rem" color="#6b7fa0">
                        <Text as="span" color="#f0f0f5" fontWeight={600}>Step 1:</Text> Go to{' '}
                        <Text as="span" color="#00c3ff">cloudinary.com</Text> → Sign up free (no credit card)
                      </Text>
                      <Text fontSize="0.8rem" color="#6b7fa0">
                        <Text as="span" color="#f0f0f5" fontWeight={600}>Step 2:</Text> Dashboard → copy your <Text as="span" color="#00c3ff">Cloud Name</Text>
                      </Text>
                      <Text fontSize="0.8rem" color="#6b7fa0">
                        <Text as="span" color="#f0f0f5" fontWeight={600}>Step 3:</Text> Settings → Upload → <Text as="span" color="#00c3ff">Add upload preset</Text> → Mode: <Text as="span" color="#f0f0f5">Unsigned</Text> → Save → copy preset name
                      </Text>
                      <Text fontSize="0.8rem" color="#6b7fa0">
                        <Text as="span" color="#f0f0f5" fontWeight={600}>Step 4:</Text> Open <Text as="span" color="#f0f0f5" fontFamily="monospace">.env</Text> in project root → fill in:
                      </Text>
                    </VStack>
                    <Box
                      w="100%"
                      bg="#07070a"
                      border="1px solid rgba(255,255,255,0.07)"
                      borderRadius="8px"
                      p={3}
                      fontFamily="monospace"
                      fontSize="0.8rem"
                      color="#00c3ff"
                    >
                      <Text>REACT_APP_CLOUDINARY_CLOUD_NAME=<Text as="span" color="#f59e0b">dxxxxxxxx</Text></Text>
                      <Text>REACT_APP_CLOUDINARY_UPLOAD_PRESET=<Text as="span" color="#f59e0b">your_preset_name</Text></Text>
                    </Box>
                    <Text fontSize="0.78rem" color="#6b7fa0">
                      Then restart the dev server (<Text as="span" fontFamily="monospace" color="#f0f0f5">npm start</Text>) and uploads will work instantly. Free plan gives 25GB storage.
                    </Text>
                    <Button
                      size="sm"
                      bg="rgba(245,158,11,0.1)"
                      color="#f59e0b"
                      border="1px solid rgba(245,158,11,0.3)"
                      borderRadius="6px"
                      fontSize="0.78rem"
                      px={3}
                      _hover={{ bg: 'rgba(245,158,11,0.18)' }}
                      onClick={() => setUploadError(null)}
                    >
                      Got it
                    </Button>
                  </VStack>
                )}
                {uploadError === 'failed' && (
                  <VStack align="flex-start" gap={1.5}>
                    <Text fontSize="0.85rem" fontWeight={700} color="#ff6b6b">❌ Upload failed</Text>
                    <Text fontSize="0.8rem" color="#6b7fa0">Check your Cloud Name and Upload Preset in the <Text as="span" fontFamily="monospace" color="#f0f0f5">.env</Text> file. Make sure the preset is set to <Text as="span" color="#f0f0f5">Unsigned</Text> in Cloudinary settings.</Text>
                    <Button
                      size="sm"
                      bg="rgba(0,195,255,0.1)"
                      color="#00c3ff"
                      border="1px solid rgba(0,195,255,0.3)"
                      borderRadius="6px"
                      fontSize="0.78rem"
                      px={3}
                      _hover={{ bg: 'rgba(0,195,255,0.18)' }}
                      onClick={() => setUploadError(null)}
                    >
                      Dismiss
                    </Button>
                  </VStack>
                )}
              </Box>
            )}

            {/* Divider + manual URL fallback */}
            <Flex align="center" gap={3} my={4}>
              <Box flex={1} h="1px" bg="rgba(255,255,255,0.07)" />
              <Text fontSize="0.72rem" color="#6b7fa0" textTransform="uppercase" letterSpacing="0.1em">or paste a link</Text>
              <Box flex={1} h="1px" bg="rgba(255,255,255,0.07)" />
            </Flex>

            <Input
              placeholder="Paste Google Drive / Dropbox / direct URL"
              value={form.fileUrl}
              onChange={e => setForm(f => ({ ...f, fileUrl: e.target.value }))}
              bg="#0e1520"
              border="1px solid rgba(0,195,255,0.15)"
              color="#f0f0f5"
              borderRadius="8px"
              fontSize="0.85rem"
              _placeholder={{ color: '#6b7fa0' }}
              _focus={{ borderColor: '#00c3ff' }}
            />
          </Box>

          {/* Action Buttons */}
          <Flex gap={3}>
            <Button
              bg="#00c3ff"
              color="#000"
              fontWeight={700}
              px={5}
              py={2.5}
              borderRadius="8px"
              isDisabled={uploadProgress !== null}
              _hover={{ transform: 'translateY(-1px)', boxShadow: '0 8px 24px rgba(0,195,255,0.4)' }}
              onClick={handleSave}
            >
              {editingId ? '✅ Update Document' : '💾 Save Document'}
            </Button>
            {editingId && (
              <Button
                bg="transparent"
                color="#6b7fa0"
                px={5}
                py={2.5}
                borderRadius="8px"
                _hover={{ color: '#ff4747' }}
                onClick={handleCancel}
              >
                Cancel
              </Button>
            )}
          </Flex>
        </Box>
      </AdminCard>

      {/* Documents Table */}
      <Box mt={6}>
        <AdminCard>
          {documents.length === 0 ? (
            <Box py={12} textAlign="center" color="#6b7fa0">
              <Text fontSize="2.5rem" mb={2}>📂</Text>
              <Text>No documents yet. Upload one above.</Text>
            </Box>
          ) : (
            <Box overflowX="auto">
              <Table variant="unstyled" size="sm">
                <Thead>
                  <Tr>
                    <Th py={3} px={4} fontSize="0.78rem" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">Document</Th>
                    <Th py={3} px={4} fontSize="0.78rem" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">Video</Th>
                    <Th py={3} px={4} fontSize="0.78rem" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">Category</Th>
                    <Th py={3} px={4} fontSize="0.78rem" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">Unique Link</Th>
                    <Th py={3} px={4} fontSize="0.78rem" color="#6b7fa0" borderBottom="1px solid rgba(255,255,255,0.07)">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {documents.map(doc => (
                    <Tr key={doc.id} _hover={{ bg: 'rgba(0,195,255,0.02)' }}>
                      <Td py={3.5} px={4} borderBottom="1px solid rgba(255,255,255,0.04)">
                        <Text fontSize="0.88rem" fontWeight={600}>{doc.emoji} {doc.title}</Text>
                        {doc.description && <Text fontSize="0.75rem" color="#6b7fa0" noOfLines={1}>{doc.description}</Text>}
                      </Td>
                      <Td py={3.5} px={4} borderBottom="1px solid rgba(255,255,255,0.04)">
                        <Text fontSize="0.8rem" color="#6b7fa0" noOfLines={1}>{doc.videoTitle || '—'}</Text>
                      </Td>
                      <Td py={3.5} px={4} borderBottom="1px solid rgba(255,255,255,0.04)">
                        <Box px={2.5} py={0.5} bg="rgba(0,195,255,0.15)" color="#00c3ff" borderRadius="full" fontSize="0.7rem" fontWeight={600} display="inline-block">{doc.category}</Box>
                      </Td>
                      <Td py={3.5} px={4} borderBottom="1px solid rgba(255,255,255,0.04)">
                        <Button
                          size="sm"
                          bg={copied === doc.id ? 'rgba(0,195,255,0.15)' : 'rgba(255,255,255,0.05)'}
                          color={copied === doc.id ? '#00c3ff' : '#6b7fa0'}
                          border="1px solid"
                          borderColor={copied === doc.id ? 'rgba(0,195,255,0.4)' : 'rgba(255,255,255,0.1)'}
                          borderRadius="6px"
                          fontSize="0.75rem"
                          px={3}
                          py={1.5}
                          _hover={{ bg: 'rgba(0,195,255,0.1)', color: '#00c3ff', borderColor: 'rgba(0,195,255,0.3)' }}
                          onClick={() => copyLink(doc.id)}
                        >
                          {copied === doc.id ? '✓ Copied!' : '🔗 Copy Link'}
                        </Button>
                      </Td>
                      <Td py={3.5} px={4} borderBottom="1px solid rgba(255,255,255,0.04)">
                        <Flex gap={2}>
                          <Button
                            size="sm"
                            bg="transparent"
                            border="1px solid rgba(255,255,255,0.07)"
                            color="#f0f0f5"
                            borderRadius="6px"
                            fontSize="0.75rem"
                            px={3}
                            _hover={{ borderColor: '#00c3ff', color: '#00c3ff' }}
                            onClick={() => handleEdit(doc)}
                          >
                            ✏️
                          </Button>
                          <Button
                            size="sm"
                            bg="#ff4747"
                            color="#fff"
                            borderRadius="6px"
                            fontSize="0.75rem"
                            px={3}
                            _hover={{ bg: '#ff2020' }}
                            onClick={() => deleteDocument(doc.id)}
                          >
                            🗑
                          </Button>
                        </Flex>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </AdminCard>
      </Box>
    </Box>
  );
}
